import { useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getReceipts, uploadReceipts,
  updateReceipt, matchReceipt, unlinkReceipt, deleteReceipt,
  searchTransactions,
} from '../../api'
import { PBBadge, CatPill, LinkIcon, SortableTh, multiSortBy, promoteSort } from '../../components/txDisplay'
import type { SortKey } from '../../components/txDisplay'
import { useBusinessContext } from '../../context/BusinessContext'
import type { Receipt, ReceiptStatus, ReceiptUpdatePayload, Transaction } from '../../types'

const money = (v: number | null | undefined, currency = 'CAD') =>
  v == null ? '—' : v.toLocaleString('en-CA', { style: 'currency', currency })
const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'
const fmtDateInput = (d: string | null | undefined) =>
  d ? new Date(d).toISOString().slice(0, 10) : ''
const pct = (v: number | null | undefined) =>
  v == null ? '' : `${Math.round(v * 100)}%`

function statusStyle(status: ReceiptStatus): React.CSSProperties {
  const map: Record<string, [string, string]> = {
    PendingExtraction: ['rgba(251,191,36,0.15)',  '#b45309'],
    Extracted:         ['rgba(59,130,246,0.15)',  '#1d4ed8'],
    Matched:           ['rgba(16,185,129,0.15)',  '#059669'],
    NeedsReview:       ['rgba(245,158,11,0.15)',  '#d97706'],
    Untagged:          ['rgba(148,163,184,0.15)', 'var(--pb-muted)'],
    ExtractionFailed:  ['rgba(239,68,68,0.15)',   '#dc2626'],
  }
  const [bg, color] = map[status] ?? map['Untagged']
  return { display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.65rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: bg, color }
}

function statusLabel(s: ReceiptStatus) {
  const labels: Record<ReceiptStatus, string> = {
    PendingExtraction: 'Processing…', Extracted: 'Extracted', Matched: 'Matched',
    NeedsReview: 'Needs Review', Untagged: 'Untagged', ExtractionFailed: 'Failed',
  }
  return labels[s] ?? s
}

const ALLOWED_TYPES = ['image/jpeg','image/png','image/webp','image/gif','application/pdf','image/tiff']

function UploadZone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const handle = (files: FileList | null) => {
    if (!files) return
    const valid = Array.from(files).filter(f => ALLOWED_TYPES.includes(f.type))
    if (valid.length) onFiles(valid)
  }
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files) }}
      style={{ border: `2px dashed ${dragging ? 'var(--pb-primary)' : 'var(--pb-border)'}`, borderRadius: 12, padding: '2rem 1.5rem', textAlign: 'center', cursor: 'pointer', background: dragging ? 'rgba(99,102,241,0.04)' : 'transparent', transition: 'all 0.15s' }}
    >
      <i className="bi bi-cloud-upload" style={{ fontSize: '2rem', color: 'var(--pb-primary)', display: 'block', marginBottom: '0.5rem' }} />
      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Drop receipts here or click to browse</div>
      <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)' }}>JPEG · PNG · WebP · PDF · TIFF · up to 20 MB each · max 10 at a time</div>
      <input ref={inputRef} type="file" multiple accept={ALLOWED_TYPES.join(',')} style={{ display: 'none' }} onChange={e => handle(e.target.files)} />
    </div>
  )
}

const tdStyle: React.CSSProperties = { padding: '0.5rem 0.6rem', borderBottom: '1px solid var(--pb-border)', fontSize: '0.82rem', verticalAlign: 'middle' }

function ReceiptRow({ r, selected, onClick }: { r: Receipt; selected: boolean; onClick: () => void }) {
  return (
    <tr onClick={onClick} style={{ cursor: 'pointer', background: selected ? 'rgba(99,102,241,0.06)' : '', transition: 'background 0.1s' }}
      onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.03)' }}
      onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLElement).style.background = '' }}>
      <td style={tdStyle}>{fmtDate(r.receiptDate ?? r.uploadDate)}</td>
      <td style={{ ...tdStyle, maxWidth: 180 }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{r.merchantName ?? r.originalFileName}</span></td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--pb-muted)' }}>{money(r.subtotalAmount, r.currency ?? 'CAD')}</td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--pb-muted)' }}>{money(r.taxAmount, r.currency ?? 'CAD')}</td>
      <td style={{ ...tdStyle, width: 64, maxWidth: 64, color: 'var(--pb-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.taxType ?? '—'}</td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--pb-muted)' }}>{money(r.tipAmount, r.currency ?? 'CAD')}</td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(r.totalAmount, r.currency ?? 'CAD')}</td>
      <td style={{ ...tdStyle, width: 64, maxWidth: 64, color: 'var(--pb-muted)' }}>{r.currency ?? '—'}</td>
      <td style={tdStyle}><span style={statusStyle(r.status)}>{statusLabel(r.status)}</span></td>
    </tr>
  )
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.82rem' }}>
      <span style={{ color: 'var(--pb-muted)', width: 72, flexShrink: 0 }}>{label}</span>
      <span>{value ?? '—'}</span>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // Horizontal layout mirrors the view-mode KV row so toggling Edit barely shifts things.
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.82rem' }}>
      <label style={{ color: 'var(--pb-muted)', width: 72, flexShrink: 0 }}>{label}</label>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

function LinkTransactionModal({ transactions, receipt, onMatch, onClose, loading }: {
  transactions: Transaction[]; receipt: Receipt; onMatch: (id: string) => void; onClose: () => void; loading: boolean
}) {
  const [q, setQ] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState<10 | 30 | 60 | 'all'>(10)
  const [matchAmount, setMatchAmount] = useState(true)
  const [unlinkedOnly, setUnlinkedOnly] = useState(true)
  type LinkSortF = 'date' | 'merchant' | 'category' | 'tags' | 'amount'
  const [sortKeys, setSortKeys] = useState<SortKey<LinkSortF>[]>([{ field: 'date', dir: 'desc' }])
  const linkSort = (f: LinkSortF) => setSortKeys(prev => promoteSort(prev, f, ['date']))

  const receiptTotal = receipt.totalAmount
  const receiptTime  = receipt.receiptDate ? new Date(receipt.receiptDate).getTime() : null

  const filtered = transactions.filter(t => {
    // Text search
    if (q) {
      const s = q.toLowerCase()
      const hit = (t.merchantDisplayName ?? t.merchant ?? '').toLowerCase().includes(s)
        || (t.date ?? '').includes(s) || (t.category ?? '').toLowerCase().includes(s)
        || (t.tag ?? '').toLowerCase().includes(s)
      if (!hit) return false
    }
    // Date range: ± N days from the receipt date
    if (dateRange !== 'all' && receiptTime !== null && t.date) {
      const diffDays = Math.abs(new Date(t.date).getTime() - receiptTime) / 86_400_000
      if (diffDays > dateRange) return false
    }
    // Amount match: transaction amount equals the receipt total
    if (matchAmount && receiptTotal != null) {
      if (Math.abs(Math.abs(t.charges ?? 0) - receiptTotal) > 0.005) return false
    }
    // Unlinked only: transaction not already linked to a receipt/invoice
    if (unlinkedOnly && t.linkStatus && t.linkStatus !== 'Unlinked') return false
    return true
  })
  const sortedRows = multiSortBy(filtered, sortKeys, (t, f): string | number => {
    switch (f) {
      case 'date':     return t.date ?? ''
      case 'merchant': return (t.merchantDisplayName ?? t.merchant ?? '').toLowerCase()
      case 'category': return (t.category ?? '').toLowerCase()
      case 'tags':     return (t.tag ?? '').toLowerCase()
      case 'amount':   return Math.abs(t.charges ?? 0)
    }
  })
  const modal = (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onClose} />
      <div className="modal fade show" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60, paddingBottom: 24, zIndex: 1050 }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
        <div className="modal-dialog modal-xl" style={{ margin: 0, width: '95%', maxWidth: 1100, maxHeight: 'calc(100vh - 84px)', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-content" style={{ background: 'var(--pb-modal-solid)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', color: 'var(--pb-text)', border: '1px solid var(--pb-border)', boxShadow: 'var(--pb-glass-shadow)', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: 'calc(100vh - 84px)' }}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--pb-border)', background: 'transparent', borderRadius: '16px 16px 0 0', flexShrink: 0 }}>
              <h5 className="modal-title" style={{ fontWeight: 700, fontSize: '1rem' }}>
                <i className="bi bi-link-45deg me-2" style={{ color: 'var(--pb-primary)' }} />Link to Transaction
              </h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--pb-border)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <input className="pb-input" placeholder="Search by merchant, date, category or tag…" value={q} autoFocus onChange={e => setQ(e.target.value)} style={{ fontSize: '0.85rem', flex: '1 1 220px', minWidth: 180 }} />

              {/* Date range: ± days from receipt date */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--pb-muted)', whiteSpace: 'nowrap' }} title={receipt.receiptDate ? `Receipt date: ${fmtDate(receipt.receiptDate)}` : 'No receipt date'}>± Days</span>
                <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
                  {([10, 30, 60, 'all'] as const).map(d => (
                    <button key={d} className={`pb-chart-sel-btn${dateRange === d ? ' active' : ''}`} onClick={() => setDateRange(d)}>
                      {d === 'all' ? 'All' : d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount match toggle */}
              <button
                type="button"
                onClick={() => setMatchAmount(v => !v)}
                title={receiptTotal != null ? `Receipt total: ${money(receiptTotal, receipt.currency ?? 'CAD')}` : 'Receipt has no total'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap',
                  fontSize: '0.78rem', fontWeight: 600, padding: '0.3rem 0.7rem', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${matchAmount ? 'rgba(99,102,241,0.45)' : 'var(--pb-border)'}`,
                  background: matchAmount ? 'rgba(99,102,241,0.15)' : 'transparent',
                  color: matchAmount ? 'var(--pb-primary)' : 'var(--pb-muted)',
                }}>
                <i className={`bi ${matchAmount ? 'bi-toggle-on' : 'bi-toggle-off'}`} />Amount match
              </button>

              {/* Unlinked only toggle */}
              <button
                type="button"
                onClick={() => setUnlinkedOnly(v => !v)}
                title="Show only transactions not already linked to a receipt or invoice"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap',
                  fontSize: '0.78rem', fontWeight: 600, padding: '0.3rem 0.7rem', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${unlinkedOnly ? 'rgba(99,102,241,0.45)' : 'var(--pb-border)'}`,
                  background: unlinkedOnly ? 'rgba(99,102,241,0.15)' : 'transparent',
                  color: unlinkedOnly ? 'var(--pb-primary)' : 'var(--pb-muted)',
                }}>
                <i className={`bi ${unlinkedOnly ? 'bi-toggle-on' : 'bi-toggle-off'}`} />Unlinked only
              </button>
            </div>
            <div className="modal-body" style={{ padding: 0, overflowY: 'auto', flex: '1 1 auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: 42, borderBottom: '2px solid var(--pb-border)', background: 'var(--pb-modal-solid)', position: 'sticky', top: 0, zIndex: 1 }} />
                    {([['Date', 'date'], ['Merchant', 'merchant'], ['Category', 'category'], ['Tags', 'tags'], ['Amount', 'amount']] as const).map(([label, field]) => (
                      <SortableTh key={field} label={label} field={field} sortKeys={sortKeys} onSort={() => linkSort(field)} right={field === 'amount'}
                        style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--pb-modal-solid)' }} />
                    ))}
                    <th style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--pb-muted)', padding: '0.5rem 0.75rem', textAlign: 'center', borderBottom: '2px solid var(--pb-border)', background: 'var(--pb-modal-solid)', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--pb-muted)', fontStyle: 'italic' }}>No transactions found</td></tr>
                  ) : sortedRows.map(t => {
                    const tags = t.tag ? t.tag.split(',').map(s => s.trim()).filter(Boolean) : []
                    const isIncome = (t.charges ?? 0) < 0
                    const isSel = selectedId === t.id
                    return (
                      <tr key={t.id} onClick={e => { if (loading) return; Array.from(e.currentTarget.children).forEach((td: Element) => ((td as HTMLElement).style.background = '')); setSelectedId(isSel ? null : t.id) }} style={{ cursor: loading ? 'default' : 'pointer', borderBottom: '1px solid var(--pb-border)', background: isSel ? 'rgba(99,102,241,0.10)' : undefined }}
                        onMouseEnter={e => { if (!isSel) Array.from(e.currentTarget.children).forEach((td: Element) => ((td as HTMLElement).style.background = 'rgba(99,102,241,0.04)')) }}
                        onMouseLeave={e => { if (!isSel) Array.from(e.currentTarget.children).forEach((td: Element) => ((td as HTMLElement).style.background = '')) }}>
                        <td style={{ padding: '0.55rem 0.75rem', textAlign: 'center', verticalAlign: 'middle' }}>
                          <span style={{ display: 'inline-flex', width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${isSel ? '#6366f1' : 'var(--pb-border)'}`, background: isSel ? '#6366f1' : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                            {isSel && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                          </span>
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', whiteSpace: 'nowrap', verticalAlign: 'middle', color: 'var(--pb-muted)', fontSize: '0.82rem' }}>{t.date ?? '—'}</td>
                        <td style={{ padding: '0.55rem 0.75rem', verticalAlign: 'middle', maxWidth: 220 }}><span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{t.merchantDisplayName ?? t.merchant ?? '—'}</span></td>
                        <td style={{ padding: '0.55rem 0.75rem', verticalAlign: 'middle', fontSize: '0.82rem' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <PBBadge businessId={t.primaryLineBusinessId} />
                            <CatPill category={t.category} subCategory={t.subCategory} />
                          </span>
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', verticalAlign: 'middle', maxWidth: 180 }}>
                          {tags.length > 0 ? tags.map(tg => <span key={tg} style={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: 5, marginRight: '0.2rem', background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)' }}>{tg}</span>) : <span style={{ color: 'var(--pb-muted)' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'middle', color: isIncome ? '#16a34a' : 'inherit' }}>{'$' + Math.abs(t.charges ?? 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.55rem 0.75rem', textAlign: 'center', verticalAlign: 'middle' }}><LinkIcon status={t.linkStatus} /></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="modal-footer" style={{ borderTop: '1px solid var(--pb-border)', flexShrink: 0, gap: '0.5rem' }}>
              <button className="btn btn-light btn-sm" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={!selectedId || loading} onClick={() => selectedId && onMatch(selectedId)}>
                {loading
                  ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                  : <><i className="bi bi-link-45deg me-1" />Link selected transaction</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
  return createPortal(modal, document.body)
}

function DetailPanel({ receipt, allTransactions, onClose, onUpdated, businessId }: {
  receipt: Receipt; allTransactions: Transaction[]; onClose: () => void; onUpdated: (r: Receipt) => void; businessId: number
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [form, setForm] = useState<ReceiptUpdatePayload>({
    merchantName: receipt.merchantName, receiptDate: fmtDateInput(receipt.receiptDate),
    subtotalAmount: receipt.subtotalAmount, taxAmount: receipt.taxAmount, taxType: receipt.taxType,
    tipAmount: receipt.tipAmount, totalAmount: receipt.totalAmount, currency: receipt.currency,
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['receipts', businessId] })
  const updateMut = useMutation({ mutationFn: (p: ReceiptUpdatePayload) => updateReceipt(receipt.receiptId, p), onSuccess: r => { onUpdated(r); setEditing(false); invalidate() } })
  const matchMut  = useMutation({ mutationFn: (txId: string) => matchReceipt(receipt.receiptId, txId), onSuccess: r => { onUpdated(r); setLinkModalOpen(false); invalidate() } })
  const unlinkMut = useMutation({ mutationFn: () => unlinkReceipt(receipt.receiptId), onSuccess: r => { onUpdated(r); invalidate() } })
  const deleteMut = useMutation({ mutationFn: () => deleteReceipt(receipt.receiptId), onSuccess: () => { onClose(); invalidate() } })
  const isPdf = receipt.fileType === 'application/pdf'
  return (
    <>
    {linkModalOpen && <LinkTransactionModal transactions={allTransactions} receipt={receipt} onMatch={id => matchMut.mutate(id)} onClose={() => setLinkModalOpen(false)} loading={matchMut.isPending} />}
    <div style={{ width: 460, flexShrink: 0, borderLeft: '1px solid var(--pb-border)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.88rem' }}>{receipt.merchantName ?? receipt.originalFileName}</span>
        <button className="btn btn-sm btn-light" onClick={onClose}><i className="bi bi-x" /></button>
      </div>
      <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ borderRadius: 8, overflow: 'hidden', background: 'var(--pb-border)', textAlign: 'center' }}>
          {isPdf
            ? <a href={receipt.blobPath} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 110, gap: 8, color: 'var(--pb-primary)', textDecoration: 'none' }}><i className="bi bi-file-earmark-pdf" style={{ fontSize: '2.5rem' }} /><span style={{ fontSize: '0.82rem' }}>Open PDF</span></a>
            : <img src={receipt.blobPath} alt="receipt" style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain', display: 'block', margin: '0 auto' }} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={statusStyle(receipt.status)}>{statusLabel(receipt.status)}</span>
          {receipt.extractionConfidence != null && <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>Confidence: {pct(receipt.extractionConfidence)}</span>}
        </div>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <Field label="Merchant"><input className="pb-input" style={{ width: '100%' }} value={form.merchantName ?? ''} onChange={e => setForm(f => ({ ...f, merchantName: e.target.value || null }))} /></Field>
            <Field label="Date"><input className="pb-input" style={{ width: '100%' }} type="date" value={form.receiptDate ?? ''} onChange={e => setForm(f => ({ ...f, receiptDate: e.target.value || null }))} /></Field>
            <Field label="Subtotal"><input className="pb-input" style={{ width: '100%' }} type="number" step="0.01" value={form.subtotalAmount ?? ''} onChange={e => setForm(f => ({ ...f, subtotalAmount: e.target.value ? parseFloat(e.target.value) : null }))} /></Field>
            <Field label="Tax"><input className="pb-input" style={{ width: '100%' }} type="number" step="0.01" value={form.taxAmount ?? ''} onChange={e => setForm(f => ({ ...f, taxAmount: e.target.value ? parseFloat(e.target.value) : null }))} /></Field>
            <Field label="Tax Type"><input className="pb-input" style={{ width: '100%' }} value={form.taxType ?? ''} onChange={e => setForm(f => ({ ...f, taxType: e.target.value || null }))} placeholder="HST, GST/PST…" /></Field>
            <Field label="Tip"><input className="pb-input" style={{ width: '100%' }} type="number" step="0.01" value={form.tipAmount ?? ''} onChange={e => setForm(f => ({ ...f, tipAmount: e.target.value ? parseFloat(e.target.value) : null }))} /></Field>
            <Field label="Total"><input className="pb-input" style={{ width: '100%' }} type="number" step="0.01" value={form.totalAmount ?? ''} onChange={e => setForm(f => ({ ...f, totalAmount: e.target.value ? parseFloat(e.target.value) : null }))} /></Field>
            <Field label="Currency"><input className="pb-input" style={{ width: '100%' }} value={form.currency ?? ''} maxLength={10} onChange={e => setForm(f => ({ ...f, currency: e.target.value || null }))} placeholder="CAD" /></Field>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
              <button className="btn btn-primary btn-sm" onClick={() => updateMut.mutate(form)} disabled={updateMut.isPending}>Save</button>
              <button className="btn btn-light btn-sm" onClick={() => { setForm({ merchantName: receipt.merchantName, receiptDate: fmtDateInput(receipt.receiptDate), subtotalAmount: receipt.subtotalAmount, taxAmount: receipt.taxAmount, taxType: receipt.taxType, tipAmount: receipt.tipAmount, totalAmount: receipt.totalAmount, currency: receipt.currency }); setEditing(false) }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <KV label="Merchant"  value={receipt.merchantName} />
            <KV label="Date"      value={fmtDate(receipt.receiptDate)} />
            <KV label="Subtotal"  value={money(receipt.subtotalAmount, receipt.currency ?? 'CAD')} />
            <KV label="Tax"       value={`${money(receipt.taxAmount, receipt.currency ?? 'CAD')}${receipt.taxType ? ` (${receipt.taxType})` : ''}`} />
            <KV label="Tip"       value={money(receipt.tipAmount, receipt.currency ?? 'CAD')} />
            <KV label="Total"     value={<strong>{money(receipt.totalAmount, receipt.currency ?? 'CAD')}</strong>} />
            <KV label="Currency"  value={receipt.currency} />
            <button className="btn btn-light btn-sm mt-1" style={{ alignSelf: 'flex-start' }} onClick={() => setEditing(true)}><i className="bi bi-pencil me-1" />Edit</button>
          </div>
        )}
        <div style={{ borderTop: '1px solid var(--pb-border)', paddingTop: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
            <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>Transaction Link</span>
            {receipt.status !== 'Matched' && (
              <button className="btn btn-sm btn-light" onClick={() => setLinkModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem' }}>
                <i className="bi bi-link-45deg" />Link
              </button>
            )}
          </div>
          {receipt.status === 'Matched' ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', color: '#059669' }}><i className="bi bi-check-circle-fill" />Receipt linked</div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="btn btn-sm btn-light" onClick={() => setLinkModalOpen(true)} title="Link to a different transaction"><i className="bi bi-arrow-left-right" /></button>
                <button className="btn btn-sm btn-warning" onClick={() => unlinkMut.mutate()} disabled={unlinkMut.isPending} title="Unlink from transaction"><i className="bi bi-x-circle" /> Unlink</button>
              </div>
            </div>
          ) : receipt.status === 'NeedsReview' && receipt.matchedTransactionId ? (
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#d97706', marginBottom: '0.3rem' }}>SUGGESTED · {pct(receipt.matchConfidence)}</div>
              {(() => { const s = allTransactions.find(t => t.id === receipt.matchedTransactionId); return s ? (<div style={{ fontSize: '0.82rem', marginBottom: '0.4rem' }}><div style={{ fontWeight: 500 }}>{s.merchantDisplayName ?? s.merchant ?? '—'}</div><div style={{ color: 'var(--pb-muted)', fontSize: '0.78rem' }}>{fmtDate(s.date)} · {money(s.charges)}</div></div>) : null })()}
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="btn btn-sm btn-success" onClick={() => matchMut.mutate(receipt.matchedTransactionId!)} disabled={matchMut.isPending}>Confirm</button>
                <button className="btn btn-sm btn-outline-danger" onClick={() => unlinkMut.mutate()} disabled={unlinkMut.isPending}>Dismiss</button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)' }}>No transaction linked.</div>
          )}
        </div>
        <div style={{ borderTop: '1px solid var(--pb-border)', paddingTop: '0.75rem' }}>
          <button className="btn btn-sm btn-outline-danger" disabled={deleteMut.isPending} onClick={() => { if (confirm('Delete this receipt? This cannot be undone.')) deleteMut.mutate() }}>
            <i className="bi bi-trash me-1" />Delete Receipt
          </button>
        </div>
      </div>
    </div>
    </>
  )
}

type Tab = 'all' | 'untagged' | 'review' | 'matched'
const TABS: { key: Tab; label: string; filter?: ReceiptStatus[] }[] = [
  { key: 'all',      label: 'All' },
  { key: 'untagged', label: 'Untagged',     filter: ['Untagged', 'Extracted', 'ExtractionFailed'] },
  { key: 'review',   label: 'Needs Review', filter: ['NeedsReview'] },
  { key: 'matched',  label: 'Matched',      filter: ['Matched'] },
]


type ReceiptSortF = 'date' | 'merchant' | 'subtotal' | 'tax' | 'taxType' | 'tip' | 'total' | 'currency' | 'status'

export default function BizReceipts() {
  const qc = useQueryClient()
  const [tab, setTab]               = useState<Tab>('untagged')
  const [selected, setSelected]     = useState<Receipt | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [sortKeys, setSortKeys] = useState<SortKey<ReceiptSortF>[]>([{ field: 'date', dir: 'desc' }])
  const [uploading, setUploading]   = useState(false)
  const [uploadErrors, setUploadErrors] = useState<{ file: string; error: string }[]>([])
  const { selectedBusinessId: bizId } = useBusinessContext()
  const currentYear = new Date().getFullYear()

  const { data: allReceipts = [], isLoading } = useQuery({
    queryKey: ['receipts', bizId],
    queryFn:  () => getReceipts(bizId!),
    enabled:  bizId != null,
    refetchInterval: (query) => {
      const list: Receipt[] = (query.state.data as Receipt[]) ?? []
      return list.some(r => r.status === 'PendingExtraction') ? 5000 : false
    },
  })

  const { data: bizTxData } = useQuery({
    queryKey: ['biz-transactions-all'],
    queryFn:  () => searchTransactions({ isPersonal: false }),
    staleTime: 60_000,
  })
  const allTransactions = bizTxData?.items ?? []

  const tabReceipts = (() => {
    const t = TABS.find(t => t.key === tab)
    if (!t?.filter) return allReceipts
    return allReceipts.filter(r => t.filter!.includes(r.status))
  })()

  const receiptValue = (r: Receipt, f: ReceiptSortF): string | number => {
    switch (f) {
      case 'date':     return r.receiptDate ?? r.uploadDate ?? ''
      case 'merchant': return (r.merchantName ?? r.originalFileName ?? '').toLowerCase()
      case 'subtotal': return r.subtotalAmount ?? -1
      case 'tax':      return r.taxAmount ?? -1
      case 'taxType':  return (r.taxType ?? '').toLowerCase()
      case 'tip':      return r.tipAmount ?? -1
      case 'total':    return r.totalAmount ?? -1
      case 'currency': return (r.currency ?? '').toLowerCase()
      case 'status':   return r.status
    }
  }
  const sorted = multiSortBy(tabReceipts, sortKeys, receiptValue)
  const handleSort = (f: ReceiptSortF) => setSortKeys(prev => promoteSort(prev, f, ['date']))

  const tabCounts = Object.fromEntries(TABS.map(t => [
    t.key,
    t.filter ? allReceipts.filter(r => t.filter!.includes(r.status)).length : allReceipts.length,
  ])) as Record<Tab, number>

  const handleUpload = useCallback(async (files: File[]) => {
    if (!bizId) return
    setUploading(true); setUploadErrors([])
    try {
      await uploadReceipts(bizId, currentYear, files)
      qc.invalidateQueries({ queryKey: ['receipts', bizId] })
    } catch { setUploadErrors([{ file: '—', error: 'Upload failed. Please try again.' }]) }
    finally { setUploading(false) }
  }, [bizId, currentYear, qc])

  return (
    <div>
      <h1 className="pb-page-title mb-3">Receipts</h1>

      {bizId == null ? (
        <div className="pb-card">
          <div className="pb-empty" style={{ padding: '3rem 1rem' }}>
            <i className="bi bi-building" style={{ fontSize: '2rem', opacity: 0.3, display: 'block', marginBottom: '0.75rem' }} />
            <div style={{ fontWeight: 600 }}>No business found</div>
            <div style={{ fontSize: '0.88rem' }}>Set up a business in Settings first.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Controls row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div className="pb-month-pills" style={{ display: 'inline-flex' }}>
              {TABS.map(t => (
                <button key={t.key} className={`pb-month-pill${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
                  {t.label}
                  {tabCounts[t.key] > 0 && (
                    <span style={{
                      marginLeft: '0.35rem',
                      background: tab === t.key ? 'rgba(255,255,255,0.35)' : 'rgba(99,102,241,0.12)',
                      color: tab === t.key ? '#fff' : 'var(--pb-primary)',
                      borderRadius: 999, padding: '0.05rem 0.42rem',
                      fontSize: '0.68rem', fontWeight: 700,
                    }}>{tabCounts[t.key]}</span>
                  )}
                </button>
              ))}
            </div>
            <button
              className="pb-btn-primary"
              onClick={() => { setShowUpload(v => !v); setUploadErrors([]) }}
            >
              <i className="bi bi-cloud-upload me-1" style={{ fontSize: '0.8rem' }} />
              {showUpload ? 'Hide Upload' : 'Upload Receipts'}
            </button>
          </div>

          {/* Upload panel — hidden by default */}
          {showUpload && (
            <div className="pb-card mb-3" style={{ padding: '1.25rem' }}>
              {uploading
                ? <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--pb-muted)' }}><span className="spinner-border spinner-border-sm me-2" />Uploading…</div>
                : <UploadZone onFiles={handleUpload} />
              }
              {uploadErrors.length > 0 && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  {uploadErrors.map((e, i) => (
                    <div key={i} style={{ fontSize: '0.8rem', color: '#dc2626' }}><strong>{e.file}</strong>: {e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Receipts table */}
          <div className="pb-card" style={{ padding: 0, display: 'flex', overflow: 'hidden', minHeight: 400 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {isLoading ? (
                <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>
              ) : tabReceipts.length === 0 ? (
                <div className="pb-empty" style={{ padding: '3rem 1rem' }}>
                  <i className="bi bi-file-earmark-check" style={{ fontSize: '2rem', opacity: 0.3, display: 'block', marginBottom: '0.75rem' }} />
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    {tab === 'all' ? 'No receipts yet' : `No ${TABS.find(t => t.key === tab)?.label.toLowerCase()} receipts`}
                  </div>
                  {tab === 'all' && <div style={{ fontSize: '0.88rem' }}>Click "+ Upload Receipts" to get started.</div>}
                </div>
              ) : (
                <>
                  <div style={{ padding: '0.85rem 1.25rem 0.7rem', borderBottom: '1px solid var(--pb-border)' }}>
                    <div className="pb-kpi-label"><i className="bi bi-file-earmark-check me-2" />Receipts<span className="ms-2" style={{ fontSize: '0.82rem', fontWeight: 400, color: 'var(--pb-muted)' }}>({sorted.length})</span></div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr>
                          <SortableTh label="Date"            field="date"     sortKeys={sortKeys} onSort={() => handleSort('date')} />
                          <SortableTh label="Merchant / File" field="merchant" sortKeys={sortKeys} onSort={() => handleSort('merchant')} />
                          <SortableTh label="Subtotal" field="subtotal" sortKeys={sortKeys} onSort={() => handleSort('subtotal')} right />
                          <SortableTh label="Tax"      field="tax"      sortKeys={sortKeys} onSort={() => handleSort('tax')} right />
                          <SortableTh label="Tax Type" field="taxType"  sortKeys={sortKeys} onSort={() => handleSort('taxType')} style={{ width: 64 }} />
                          <SortableTh label="Tip"      field="tip"      sortKeys={sortKeys} onSort={() => handleSort('tip')} right />
                          <SortableTh label="Total"    field="total"    sortKeys={sortKeys} onSort={() => handleSort('total')} right />
                          <SortableTh label="Curr."    field="currency" sortKeys={sortKeys} onSort={() => handleSort('currency')} style={{ width: 64 }} />
                          <SortableTh label="Status"   field="status"   sortKeys={sortKeys} onSort={() => handleSort('status')} />
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map(r => (
                          <ReceiptRow key={r.receiptId} r={r} selected={selected?.receiptId === r.receiptId}
                            onClick={() => setSelected(r.receiptId === selected?.receiptId ? null : r)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            {selected && (
              <DetailPanel key={selected.receiptId} receipt={selected} allTransactions={allTransactions}
                onClose={() => setSelected(null)} onUpdated={r => setSelected(r)} businessId={bizId} />
            )}
          </div>
        </>
      )}
    </div>
  )
}
